import { BookSummary } from "@/types";
import { formatDistanceToNow } from "date-fns";
import { Clock, Eye, Star, Globe, Headphones } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";

type BookCardProps = {
  book: BookSummary;
};

const BookCard = ({ book }: BookCardProps) => {
  const hasAudio = book.summaryContents.some((content) => content.audioUrl);
  const hasMultipleLanguages = book.summaryContents.length > 1;

  return (
    <Link to={`/books/${book.id}`}>
      <div className="group flex items-center gap-3 p-3 hover:bg-muted/50 rounded-lg transition-colors duration-200 border-b border-border/50 last:border-b-0">
        {/* Cover Image */}
        <div className="relative flex-shrink-0">
          <img
            src={book.coverImage}
            alt={book.title}
            className="w-16 h-20 object-cover rounded-md group-hover:scale-105 transition-transform duration-200"
          />

          {/* Audio Badge */}
          {hasAudio && (
            <div className="absolute -top-1 -right-1">
              <Badge className="bg-primary text-primary-foreground text-xs px-1.5 py-0.5 h-5 flex items-center gap-1">
                <Headphones className="w-2.5 h-2.5" />
              </Badge>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title and Authors */}
          <div className="mb-1">
            <h3 className="font-medium text-sm leading-tight line-clamp-1 text-foreground group-hover:text-primary transition-colors">
              {book.title}
            </h3>
            <p className="text-xs text-muted-foreground line-clamp-1">
              by {book.authors.map((author) => author.name).join(", ")}
            </p>
          </div>

          {/* Categories */}
          <div className="flex flex-wrap gap-1 mb-2">
            {book.categories.slice(0, 2).map((category) => (
              <Badge
                key={category.id}
                variant="outline"
                className="text-xs px-1.5 py-0.5 h-4 border-border bg-background/50"
                title={
                  category.name_mz
                    ? `${category.name} / ${category.name_mz}`
                    : category.name
                }
              >
                {category.name}
              </Badge>
            ))}
            {book.categories.length > 2 && (
              <Badge
                variant="outline"
                className="text-xs px-1.5 py-0.5 h-4 border-border bg-background/50"
              >
                +{book.categories.length - 2}
              </Badge>
            )}
          </div>

          {/* Stats */}
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>{book.readingTime}m</span>
            </div>
            <div className="flex items-center gap-1">
              <Eye className="w-3 h-3" />
              <span>{book.viewCount.toLocaleString()}</span>
            </div>
            {book.rating > 0 && (
              <div className="flex items-center gap-1">
                <Star className="w-3 h-3 text-yellow-500 dark:text-yellow-400 fill-current" />
                <span>{book.rating}</span>
              </div>
            )}
            {hasMultipleLanguages && (
              <div className="flex items-center gap-1">
                <Globe className="w-3 h-3" />
                <span>{book.summaryContents.length}</span>
              </div>
            )}
          </div>
        </div>

        {/* Updated Time */}
        <div className="flex-shrink-0 text-xs text-muted-foreground/70 text-right">
          {formatDistanceToNow(new Date(book.updatedAt), { addSuffix: true })}
        </div>
      </div>
    </Link>
  );
};

export default BookCard;
